import React, { useState, useRef, useEffect } from 'react';

export default function App() {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Ciao! Sono il tuo assistente AI. Come posso aiutarti oggi?'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedSocio, setSelectedSocio] = useState(null);
  const [sociOptions, setSociOptions] = useState([]);
  const [searchingSoci, setSearchingSoci] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Search soci with debounce
  useEffect(() => {
    if (searchTerm.length < 3) {
      setSociOptions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSearchingSoci(true);
      try {
        const apiUrl = window.appLocalizer?.apiUrl || '/wp-json';
        const response = await fetch(`${apiUrl}/ai/v1/search-soci?q=${encodeURIComponent(searchTerm)}`);
        const data = await response.json();

        if (response.ok && data.results) {
          setSociOptions(data.results);
        }
      } catch (error) {
        console.error('Error searching soci:', error);
      } finally {
        setSearchingSoci(false);
      }
    }, 300); // Debounce 300ms

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Add welcome message when socio is selected
  useEffect(() => {
    if (selectedSocio) {
      const socioName = selectedSocio.text.split('(')[0].trim();
      const welcomeMessage = {
        role: 'assistant',
        content: `✓ Perfetto! Come posso aiutarti con ${socioName}?`
      };
      setMessages(prev => [...prev, welcomeMessage]);
    }
  }, [selectedSocio]);

  const handleSend = async (e) => {
    e.preventDefault();
    
    if (!input.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: input.trim()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      // Get API URL and nonce from WordPress
      const apiUrl = window.appLocalizer?.apiUrl || '/wp-json';
      const nonce = window.appLocalizer?.nonce || '';

      const response = await fetch(`${apiUrl}/ai/v1/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': nonce
        },
        body: JSON.stringify({
          q: userMessage.content,
          socio_id: selectedSocio?.id || null
        })
      });

      const data = await response.json();

      if (response.ok && data.answer) {
        const assistantMessage = {
          role: 'assistant',
          content: data.answer,
          chunks: data.chunks || []
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        throw new Error(data.message || 'Errore nella richiesta');
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = {
        role: 'assistant',
        content: `Mi dispiace, si è verificato un errore: ${error.message}. Assicurati che l'indicizzazione sia completata.`,
        error: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="w-full max-w-4xl flex flex-col h-[85vh] min-h-[600px] max-h-[800px] bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-5">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold">AI Chat Assistant</h2>
              <p className="text-sm text-indigo-100">Chiedi qualsiasi cosa, ti risponderò basandomi sui contenuti del sito</p>
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gradient-to-b from-gray-50 to-white">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex items-start space-x-3 ${
                message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
              }`}
            >
              {/* Avatar */}
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                message.role === 'user'
                  ? 'bg-indigo-600'
                  : 'bg-gradient-to-br from-purple-500 to-indigo-500'
              } text-white text-sm font-semibold`}>
                {message.role === 'user' ? 'T' : 'AI'}
              </div>

              {/* Message Bubble */}
              <div className={`flex-1 max-w-[75%] ${
                message.role === 'user' ? 'flex flex-col items-end' : ''
              }`}>
                <div
                  className={`rounded-2xl px-5 py-3 shadow-md ${
                    message.role === 'user'
                      ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-br-none'
                      : message.error
                      ? 'bg-red-50 text-red-800 border-2 border-red-200 rounded-bl-none'
                      : 'bg-white text-gray-800 border border-gray-100 rounded-bl-none shadow-sm'
                  }`}
                >
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                  
                  {/* Show chunks if available */}
                  {message.chunks && message.chunks.length > 0 && (
                    <div className={`mt-3 pt-3 ${
                      message.role === 'user' 
                        ? 'border-t border-white/30' 
                        : 'border-t border-gray-200'
                    }`}>
                      <p className={`text-xs font-semibold mb-2 ${
                        message.role === 'user' ? 'text-indigo-100' : 'text-gray-600'
                      }`}>📚 Fonti:</p>
                      <div className="space-y-1">
                        {message.chunks.map((chunk, idx) => (
                          <a
                            key={idx}
                            href={chunk.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`text-xs block hover:underline transition-all ${
                              message.role === 'user'
                                ? 'text-indigo-100 hover:text-white'
                                : 'text-indigo-600 hover:text-indigo-800'
                            }`}
                          >
                            🔗 {chunk.object_type} #{chunk.object_id} (relevanza: {(chunk.score * 100).toFixed(0)}%)
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <span className={`text-xs mt-1 px-2 ${
                  message.role === 'user' ? 'text-gray-500' : 'text-gray-400'
                }`}>
                  {new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 text-white text-sm font-semibold flex items-center justify-center">
                AI
              </div>
              <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-none px-5 py-3 shadow-sm">
                <div className="flex space-x-2">
                  <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                  <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <form onSubmit={handleSend} className="border-t border-gray-200 bg-white p-4 space-y-3">
          {/* Socio Selector */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">👤 Socio:</label>
            <div className="flex-1 relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cerca un socio (min 3 caratteri)..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
                disabled={loading}
              />
              {searchingSoci && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
            {selectedSocio && (
              <button
                type="button"
                onClick={() => { setSelectedSocio(null); setSearchTerm(''); setSociOptions([]); }}
                className="px-3 py-2 text-xs bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
              >
                ✕ Rimuovi
              </button>
            )}
          </div>

          {/* Selected Socio Badge */}
          {selectedSocio && (
            <div className="flex items-center space-x-2 p-2 bg-indigo-50 border border-indigo-200 rounded-lg">
              <span className="text-sm font-medium text-indigo-900">✓ Selezionato:</span>
              <span className="text-sm text-indigo-700">{selectedSocio.text}</span>
            </div>
          )}

          {/* Soci Options Dropdown */}
          {sociOptions.length > 0 && !selectedSocio && (
            <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg bg-white shadow-lg">
              {sociOptions.map((socio) => (
                <button
                  key={socio.id}
                  type="button"
                  onClick={() => {
                    setSelectedSocio(socio);
                    setSearchTerm('');
                    setSociOptions([]);
                  }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 transition-colors border-b border-gray-100 last:border-b-0"
                >
                  <span className="font-medium text-gray-900">{socio.text}</span>
                  <span className="text-xs text-gray-500 ml-2">ID: {socio.id}</span>
                </button>
              ))}
            </div>
          )}

          {/* Question Input */}
          <div className="flex items-end space-x-3">
            <div className="flex-1 relative">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={selectedSocio ? `Chiedi qualcosa su ${selectedSocio.text.split('(')[0].trim()}...` : "Scrivi la tua domanda qui..."}
                className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-gray-50 focus:bg-white"
                disabled={loading}
              />
              {input && (
                <button
                  type="button"
                  onClick={() => setInput('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none flex items-center space-x-2"
            >
              <span>Invia</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2 text-center">
            Le risposte sono generate in base ai contenuti indicizzati del sito
          </p>
        </form>
      </div>
    </div>
  );
}
